import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { debounceTime, Subject, takeUntil } from "rxjs";
import * as Validation from "../../../../shared/constants/validation.constants";
import { FormControl, FormGroup } from "@angular/forms";
import { Store } from "@ngrx/store";
import * as fromRoot from "../../../../../state/app.state";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonService } from "../../../../shared/services/common.service";
import {
  FetchClinicFilterRequest,
  FetchGroupFilterRequest,
  FetchRolesListRequest,
  FetchStatusFilterRequest
} from "../../core/manage-users.actions";
import {
  profile,
  selectedUserProfile
} from "../../../../shared/core/shared.selectors";
import { roles, sortOrderMap } from "../../../../shared/constants/common.constants";
import {
  clinicFilter,
  groupFilter,
  rolesList,
  statusFilter
} from "../../core/manage-users.selectors";
import { ManageUsersService } from '../../core/manage-users.service';
import { Filter } from '../../../../../models/filter';
import {
  FetchProfileByUserIdRequest
} from "../../../../shared/core/shared.actions";

@Component({
  selector: 'app-manage-users-filter',
  templateUrl: './manage-users-filter.component.html',
  styleUrls: ['./manage-users-filter.component.css']
})
export class ManageUsersFilterComponent implements OnInit {

  loading: boolean = false;
  clinicFilterList: Filter;
  statusFilterList: Filter;
  rolesFilterData: Filter;
  groupFilterList: Filter;
  role: number;
  selectedUserRole: number;
  selectedUserLoading: boolean = false;
  errorMessages: any = {};
  validation = Validation;
  errorMessageMap = {
    'clinic': {
      required: 'This field is required.'
    },
    'group': {
      required: 'This field is required.'
    },
    'role': {
      required: 'This field is required.'
    },
    'status': {
      required: 'This field is required.'
    },
    'search': {
      required: 'This field is required.'
    }
  }
  form = new FormGroup<any>({
    role: new FormControl({ value: null, disabled: false }),
    status: new FormControl({ value: null, disabled: false }),
    search: new FormControl({ value: null, disabled: false }),
  })
  selectedClientId: number;
  protected readonly roles = roles;
  private readonly OnDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private commonService: CommonService,
    private manageUsersService: ManageUsersService,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef
  ) {
    this.store.dispatch(FetchStatusFilterRequest());
    this.store.dispatch(FetchRolesListRequest());
  }

  ngOnInit() {
    this.store
      .select(profile)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.role = data?.role;

        if (this.role === roles['superAdmin']) {
          this.addClinicField();
          this.store.dispatch(FetchClinicFilterRequest({
            payload:{
              pageNumber: 1,
              limit: 10
            }
          }));
        } else {
          this.addGroupField()
          this.store.dispatch(FetchGroupFilterRequest({
            payload: {
              status: [1, 2],
              sortColumn: 'startDate',
              sortOrder: sortOrderMap.desc
            }
          }));
        }
      });

    this.store.select(selectedUserProfile)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(d => {
        this.selectedUserLoading = d.loading;
        if (d.profile) {
          this.selectedUserRole = d.profile.role;
          this.selectedClientId = d.profile?.clientId;

          this.addGroupField()

          if (this.role === roles['superAdmin']) {
            this.store.dispatch(FetchGroupFilterRequest({
              payload: {
                ...(this.selectedClientId ? { clientId: this.selectedClientId } : {}),
                status: [1, 2],
                sortColumn: 'startDate',
                sortOrder: sortOrderMap.desc
              }
            }));
          }


          this.handleRoleFilter();
        } else {
          this.selectedUserRole = null;
          this.selectedClientId = null;
        }
      })

    this.commonService.onNavigationEnd
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(d => {
        const params: any = this.route.snapshot.params;
        let userId = params?.clientAdminId ||
          params?.coachId ||
          params?.memberId;

        if (userId && !this.selectedUserRole) {
          this.store.dispatch(FetchProfileByUserIdRequest({
            payload: { userId }
          }))
        }
      })

    this.store
      .select((clinicFilter))
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.clinicFilterList = data;
      });

    this.store
      .select((groupFilter))
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        if (data && data.list) {
          // Format group names to include date: <group name> (mm/dd/yyyy)
          const formattedList = data.list.map(item => ({
            ...item,
            value: item.startDate 
              ? `${item.value} (${this.formatDate(item.startDate)})`
              : item.value
          }));
          // Add "Unassigned" option at the first position
          const unassignedOption = {
            key: -1,
            value: 'Unassigned',
            startDate: null
          };
          this.groupFilterList = {
            ...data,
            list: [unassignedOption, ...formattedList]
          };
        } else {
          this.groupFilterList = data;
        }
      });

    this.store
      .select((statusFilter))
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.statusFilterList = data
      });

    this.store
      .select((rolesList))
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.rolesFilterData = JSON.parse(JSON.stringify(data));
        this.handleRoleFilter();
      });

  }

  apply() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }
    // EXTRACT VALUE FROM FORM//
    const { clinic, group, status, search, role } = this.form.getRawValue();
    // PAYLOAD CREATION//
    const payload = {
      clientId: clinic,
      groupId: group,
      status,
      role,
      searchText: search
    };

    this.form.markAsPristine();

    // ACTION DISPATCH
    this.manageUsersService.onUserFilterChange.next(payload);

  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  handleRoleFilter() {
    let role = this.role;
    if (this.selectedUserRole) {
      role = this.selectedUserRole;
    }

    if (this.rolesFilterData) {
      this.rolesFilterData.list = this.rolesFilterData?.list?.filter(o => Number(o.key) >= role);
      this.cdRef.detectChanges();
    }
  }

  addClinicField() {
    this.form.addControl('clinic', new FormControl({ value: null, disabled: false }));
    this.form.addControl('searchClinic', new FormControl({ value: null, disabled: false }));
    this.form.controls['searchClinic'].valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe(search => {
        this.store.dispatch(FetchClinicFilterRequest({
          payload: {
            pageNumber: 1,
            ...(search ? { search } : {}),
            shouldInclude: this.form.value.clinic,
            limit:10
          }
        }));
      })
  }

  clinicsOnNextPage() {
    const search = this.form.controls['searchClinic']?.value || '';
    this.store.dispatch(FetchClinicFilterRequest({
      payload: {
        pageNumber: this.clinicFilterList.pageNumber + 1,
        ...(search ? { search } : {}),
        shouldInclude: this.form.value.clinic,
        limit:10
      }
    }));
  }

  addGroupField() {
    this.form.addControl('group', new FormControl({ value: null, disabled: false }));
    this.form.addControl('searchGroup', new FormControl({ value: null, disabled: false }));
    this.form.controls['searchGroup'].valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe(search => {
        this.store.dispatch(FetchGroupFilterRequest({
          payload: {
            pageNumber: 1,
            shouldInclude: this.form.value.group,
            ...(this.selectedClientId ? { clientId: this.selectedClientId } : {}),
            ...(search ? { search } : {}),
            status: [1, 2],
            sortColumn: 'startDate',
            sortOrder: sortOrderMap.desc
          }
        }));
      })
  }

  groupsOnNextPage() {
    const search = this.form.controls['searchGroup']?.value || '';
    this.store.dispatch(FetchGroupFilterRequest({
      payload: {
        pageNumber: this.groupFilterList.pageNumber + 1,
        shouldInclude: this.form.value.group,
        ...(this.selectedClientId ? { clientId: this.selectedClientId } : {}),
        ...(search ? { search } : {}),
        status: [1, 2],
        sortColumn: 'startDate',
        sortOrder: sortOrderMap.desc
      }
    }));
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

}
